<template lang="pug">
InventoryTab(name="product" :error="fetchError" @new="newProduct" :canView="userCanViewProducts" :canAddNew="userCanAddEditProduct")
  template(v-slot:filters)
    BriqInput(v-model="query" hideOptionalMarker placeholder="Search"  data-test-id="search-products-input")
    BriqCheckbox.show-inactive(label="Show inactive products" v-model="showInactive")
  template(v-slot:overview)
    BriqDataTable(
      :data="tableData"
      :filter="filter"
      :allowDelete="userCanDeleteProduct"
      :allowEdit="userCanAddEditProduct"
      :loading="isFetchingProducts"
      :initialSort="initialSort"
      @edit="editProduct"
      @click="clickProduct"
      @delete="showDeleteDialog"
      data-test-id="products-data-table"
      )
      template(v-slot:BasePrice="{ item }")
        div {{ displayProductPrice(item.BasePrice) }}
      template(v-slot:PriceType="{ item }")
        div {{ displayPriceType(item) }}
      template(v-slot:Enabled="{ item }")
        div {{ isProductActive(item) }}
    ProductSlideInPanel(
      :focusedItem="focusedItem"
      :sidePanelIsOpen="sidePanelIsOpen"
      :allowEdit="userCanAddEditProduct"
      @edit="editProduct"
      @close="sidePanelIsOpen = false"
      )
    DeleteProductDialog(
      :showDeleteProductDialog="showDeleteProductDialog"
      @close="closeDeleteProductDialog()"
      @delete="deleteProduct()"
      :deletingProduct="deletingProduct"
      :productToBeDeleted="productToBeDeleted"
    )
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import BriqDataTable from '@v/components/BriqDataTable.vue'
import InventoryTab from '../InventoryTab.vue'
import ProductSlideInPanel from './ProductSlideInPanel.vue'
import DeleteProductDialog from './DeleteProductDialog.vue'
import { parseAxiosException } from '@/helpers/exceptionParser.js'
import Permission from '@v/models/UserPermissionEnum.js'
import {
  always,
  toLower,
  includes,
  either,
  propSatisfies,
  pipe,
  propEq,
  both,
  ifElse,
  prop,
} from 'ramda'

export default {
  name: 'Products',
  components: { BriqDataTable, InventoryTab, ProductSlideInPanel, DeleteProductDialog },
  data() {
    return {
      query: null,
      showInactive: false,
      focusedItem: null,
      sidePanelIsOpen: false,
      showDeleteProductDialog: false,
      productToBeDeleted: null,
      deletingProduct: false,
      initialSort: {
        sort: 'asc',
        sortField: 'NameInBriq',
      },
    }
  },
  computed: {
    ...mapState('inventory', ['products', 'isFetchingProducts', 'fetchError']),
    ...mapGetters('briqState', ['userHasPermission']),
    tableData() {
      return {
        items: this.products || [],
        headers: [
          { field: 'NameInBriq', label: 'Product' },
          { field: 'ResourceName', label: 'Resource' },
          { field: 'BasePrice', label: 'Base Price' },
          { field: 'PriceType', label: 'Price Type' },
          { field: 'Enabled', label: 'Active' },
        ],
      }
    },
    userCanAddEditProduct() {
      return this.userHasPermission(Permission.API_Admin_Product_Create)
    },
    userCanDeleteProduct() {
      return this.userHasPermission(Permission.API_Admin_Product_Delete)
    },
    userCanViewProducts() {
      return this.userHasPermission(Permission.API_Admin_Product_Index)
    },
    filter() {
      const byQuery = query => {
        if (!query) return always(true)
        const containsQuery = pipe(toLower, includes)(query)
        const propContainsQuery = propSatisfies(pipe(toLower, containsQuery))
        return either(propContainsQuery('NameInBriq'), propContainsQuery('ResourceName'))
      }
      const byEnabled = !this.showInactive
        ? both(propEq('Enabled', true), propEq('IsActive', true))
        : always(true)
      return both(byQuery(this.query), byEnabled)
    },
  },
  methods: {
    ...mapActions('inventory', ['maybeFetchProducts', 'removeProduct']),
    newProduct() {
      this.$router.push({ name: 'CreateProduct' })
    },
    editProduct({ item, event }) {
      if (event.ctrlKey || event.metaKey) this.$openTab(`inventory/products/${item.Key}/edit`)
      else this.$router.push({ name: 'EditProduct', params: { productKey: item.Key } })
    },
    showDeleteDialog(product) {
      this.showDeleteProductDialog = true
      this.productToBeDeleted = product
    },
    clickProduct({ item, event }) {
      if (event.ctrlKey || event.metaKey) this.$openTab(`inventory/products/${item.Key}/edit`)
      else {
        this.focusedItem = item
        this.sidePanelIsOpen = true
      }
    },
    displayProductPrice(priceObject) {
      return pipe(
        ifElse(prop('DisplayIncludingTax'), prop('IncludingTax'), prop('ExcludingTax')),
        this.$formatCurrency,
      )(priceObject)
    },
    parseBooleanValue(item, property) {
      return item[property] ? 'Yes' : 'No'
    },
    isProductActive(item) {
      return item.Enabled && item.IsActive ? 'Yes' : 'No'
    },
    displayPriceType(item) {
      return item.PriceType.replace(/([A-Z])/g, ' $1').replace(/^./, function(str) {
        return str.toUpperCase()
      })
    },
    closeDeleteProductDialog() {
      this.showDeleteProductDialog = false
      this.productToBeDeleted = null
      this.deletingProduct = false
    },
    async deleteProduct() {
      this.deletingProduct = true
      try {
        await this.$apiClient.post(`products/${this.productToBeDeleted.Key}/delete`)

        this.$notifySuccess('Product was deleted successfully')
        this.removeProduct(this.productToBeDeleted.Key)
        this.closeDeleteProductDialog()
      } catch (e) {
        const parsedException = parseAxiosException(e)
        this.$logError(`Error expected to originate from ${parsedException.origin}`)
        this.$logError(parsedException.data)
        this.$notifyError('Something went wrong')
        this.closeDeleteProductDialog()
      }
    },
  },
  mounted() {
    this.maybeFetchProducts()
  },
}
</script>

<style scoped lang="stylus">
.show-inactive
  min-width 300px
</style>
